import { Icon, type IconName } from './Icon';
import './ControlBar.css';

interface ControlBarProps {
  micEnabled: boolean;
  cameraEnabled: boolean;
  screenShareEnabled: boolean;
  screenShareSupported: boolean;
  chatOpen: boolean;
  peopleOpen: boolean;
  unreadChat: number;
  participantCount: number;
  recordingSupported: boolean;
  recording: boolean;
  recordingSaving: boolean;
  /** Организатор ли участник: запись и режим презентации только у него. */
  isHost: boolean;
  presentation: boolean;

  onToggleMic: () => void;
  onToggleCamera: () => void;
  onToggleScreenShare: () => void;
  onToggleRecording: () => void;
  onTogglePresentation: () => void;
  onToggleChat: () => void;
  onTogglePeople: () => void;
  onLeave: () => void;
}

export function ControlBar(props: ControlBarProps) {
  return (
    <div className="controls">
      <div className="controls__group">
        <ControlButton
          icon={props.micEnabled ? 'mic' : 'micOff'}
          label={props.micEnabled ? 'Выключить микрофон' : 'Включить микрофон'}
          danger={!props.micEnabled}
          onClick={props.onToggleMic}
        />
        <ControlButton
          icon={props.cameraEnabled ? 'cam' : 'camOff'}
          label={props.cameraEnabled ? 'Выключить камеру' : 'Включить камеру'}
          danger={!props.cameraEnabled}
          onClick={props.onToggleCamera}
        />
        {props.screenShareSupported && (
          <ControlButton
            icon="screen"
            label={
              props.screenShareEnabled ? 'Остановить демонстрацию' : 'Показать экран'
            }
            active={props.screenShareEnabled}
            onClick={props.onToggleScreenShare}
          />
        )}
        {/*
          Режим презентации переключает только организатор — у остальных
          кнопки нет вовсе. Отключённая кнопка была бы хуже: она обещает
          действие, которого не будет.
        */}
        {props.isHost && (
          <ControlButton
            icon="present"
            label={
              props.presentation ? 'Выключить режим презентации' : 'Включить режим презентации'
            }
            active={props.presentation}
            onClick={props.onTogglePresentation}
          />
        )}
        {/*
          Запись — тоже право организатора. Это ограничение внутри сервиса:
          записать происходящее у себя на машине человек может и без нас,
          но общая запись встречи ведётся с одного места и одним человеком.
        */}
        {props.isHost && props.recordingSupported && (
          <ControlButton
            icon={props.recording ? 'recordStop' : 'record'}
            label={
              props.recordingSaving
                ? 'Готовим файл записи'
                : props.recording
                  ? 'Остановить запись и скачать'
                  : 'Записать встречу'
            }
            danger={props.recording}
            disabled={props.recordingSaving}
            onClick={props.onToggleRecording}
          />
        )}
      </div>

      <div className="controls__group">
        <ControlButton
          icon="people"
          label="Участники"
          active={props.peopleOpen}
          badge={props.participantCount}
          onClick={props.onTogglePeople}
        />
        <ControlButton
          icon="chat"
          label="Чат"
          active={props.chatOpen}
          badge={props.unreadChat > 0 ? props.unreadChat : undefined}
          badgeTone="accent"
          onClick={props.onToggleChat}
        />
      </div>

      <div className="controls__group">
        <button type="button" className="btn btn--danger controls__leave" onClick={props.onLeave}>
          <Icon name="leave" />
          <span className="controls__leave-text">Выйти</span>
        </button>
      </div>
    </div>
  );
}

function ControlButton({
  icon,
  label,
  onClick,
  active = false,
  danger = false,
  badge,
  badgeTone = 'neutral',
  disabled = false,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
  badge?: number;
  badgeTone?: 'neutral' | 'accent';
  disabled?: boolean;
}) {
  const classes = [
    'control',
    active ? 'control--active' : '',
    danger ? 'control--danger' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
    >
      <Icon name={icon} size={21} />
      {badge !== undefined && (
        <span className={`control__badge ${badgeTone === 'accent' ? 'control__badge--accent' : ''}`}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}
